import { Component, OnInit, OnDestroy, AfterContentInit, ViewChild } from '@angular/core';
import { Location } from "@angular/common";
import { HttpClient } from '@angular/common/http';
import { TemplatePortal } from '@angular/cdk/portal';
import { Router, ActivatedRoute } from '@angular/router';
import { combineLatestWith, debounceTime, filter, tap, switchMap, map, startWith, distinctUntilChanged, delay } from 'rxjs/operators';
import { Subscription, timer, of, Observable, NEVER } from 'rxjs';
import { NgxPermissionsService } from 'ngx-permissions';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AppFacadeService } from '@services/app-facade.service';
import { VersionCheckService } from "@services/version-check.service";
import { RouterFacadeService, ViewLabel } from '@services/router-facade.service';
import { UserModel, ResellerModel, BusinessModel, LocationModel } from '@models/index';
import { AuthFacadeService } from '@services/auth/auth-facade.service';
import { BusinessFacadeService } from '@services/business/business-facade.service';
import { emptyResellerModel, emptyFacebookErrorModel } from '@utilities/data-utils';
import { AppService } from '@services/app.service';
import Swal from 'sweetalert2';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { LocationsFacadeService } from '@services/locations/locations-facade.service';
import { ReviewTargetsFacadeService } from '@services/review-targets/review-targets-facade.service';
import { FacebookFacadeService } from '@services/facebook/facebook-facade.service';
import { UsersFacadeService } from '@services/users/users-facade.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass']
})
export class AppComponent implements OnInit, AfterContentInit, OnDestroy {

  @ViewChild('trialOver') trialOverModal;
  @ViewChild('shelf') shelf;
  @ViewChild('phoneNumberUpdate') phoneNumberUpdate;

  shelf$: Observable<TemplatePortal<any>> = this.routerFacade.routerLabel$.pipe(
    map(routerLabel => typeof routerLabel != "string" ? routerLabel.shelf : null),
    tap(value => console.log("shelf$:", value))
  )
  largeShelf: boolean = false

  title = 'mulli';
  _subs: Subscription = new Subscription()
  isExecutingTask = false;
  showNavigation = true;

  selectedBusiness: BusinessModel;
  selectedLocation: LocationModel;
  businessPhoneNumber: string = '';
  businessZipCode: string = '';
  preferredLocation$ = this.locationFacadeService.getCurrentLocation$();

  loggedUser: UserModel;
  initialSelectedReseller: ResellerModel = emptyResellerModel()
  showTrialEnd: boolean = false

  serverHourPetition: any;

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private appFacadeService: AppFacadeService,
    private businessFacadeService: BusinessFacadeService,
    private _appService: AppService,
    private authFacadeService: AuthFacadeService,
    private permissionsService: NgxPermissionsService,
    private routerFacade: RouterFacadeService,
    private versionCheckService: VersionCheckService,
    private location: Location,
    private translate: TranslateService,
    private locationFacadeService: LocationsFacadeService,
    private reviewTargetFacadeService: ReviewTargetsFacadeService,
    private facebookFacadeService: FacebookFacadeService,
    private messageToast: MessageService,
    private modalService: NgbModal,
    private window: Window,
    private _http: HttpClient,
    private userFacade: UsersFacadeService,
  ) {
    this.translate.addLangs(['en', 'es']);
    this.translate.setDefaultLang('en');
    this.translate.use(navigator.language.replace(/[-].*$/, ""));
  }

  onActivate(component: Component & { routerLabel$?: Observable<ViewLabel> }) {
    this.routerFacade.routerLabels$$.next(component.routerLabel$ || of(""));
  }

  ngOnInit() {
    this.appFacadeService.clearErrorTrialOver();
    this.authFacadeService.getReseller$(this.window.location.origin);

    this.versionCheckService.initVersionCheck();
    this._subs.add(
      this._http.get("./assets/data.json").subscribe(data => {
        const time = data["inactivityTime"]
        this.appFacadeService.setInactivityTime(time)
      })
    );

    this._subs.add(
      this.router.events.subscribe(_val => {
        this.authPathValidation();

        const path = this.location.path();
        // TODO: shouldn't this be the opposite name, or used the opposite way? i.e. dontShowNavigation?
        this.showNavigation = path.includes("auth")
          || path.includes("admin-business/business")
          || path.includes("redirect-data-deletion-form")

        document.getElementById('scroll').scrollTop = 0;
      })
    );

    //this._sseSourceService.getServerSentEvent(environment.apiPath+'/notifications').pipe(
    //  // map(data => JSON.parse(data.data))
    //).subscribe(notification => {
    //  // this.appFacadeService.setLastNotification(Object.assign({}, { id: notification.payload, type: notification.type, dateCreated: notification.dateCreated }))
    //})

    this._subs.add(
      this.authFacadeService
        .getSelectedReseller$()
        .subscribe(selectedReseller => {
          this.initialSelectedReseller = selectedReseller
          this.initialSelectedReseller.favIcon !== '' ? document.getElementById('appFavicon').setAttribute('href', this.initialSelectedReseller.favIcon) : document.getElementById('appFavicon').setAttribute('href', './favicon.ico')
          document.title = this.initialSelectedReseller.name != '' ? this.initialSelectedReseller.name : 'Review App'
        })
    );

    this._subs.add(
      this.routerFacade.routerLabel$.pipe(
        map(routerLabel => typeof routerLabel != "string" ? !!routerLabel.largeShelf : false)
      ).subscribe(largeShelf => {
        this.largeShelf = largeShelf;
      })
    );
  }

  ngAfterContentInit(): void {
    this._subs.add(
      this.appFacadeService.getLoggedUser$()
        .subscribe(user => {
          this.permissionsService.addPermission(user.permissions);
          this.loggedUser = user;
          this.selectedBusiness ? this._appService.changeIntercomStatus(user, this.selectedBusiness.name) : this._appService.changeIntercomStatus(user);
        })
    )

    this._subs.add(
      this.appFacadeService.isTaskExecuting$().pipe(
        debounceTime(500)
      ).subscribe(isExecutingTask =>
        this.isExecutingTask = isExecutingTask
      )
    )

    this._subs.add(
      this.appFacadeService.getSelectedBusiness$().subscribe(business => {
        this.selectedBusiness = business;
        this._appService.changeIntercomStatus(this.loggedUser, business.name);
      })
    )

    this._subs.add(
      this.appFacadeService.getSelectedBusiness$().pipe(
        switchMap((business: BusinessModel) =>
          business.id != null && business.id != '' && business.trialMode
            ? timer(30000, 30000).pipe(map(() => business))
            : NEVER
        )
      ).subscribe(selectedBusiness => {
        this.businessFacadeService.searchBusinessById(selectedBusiness.id)
      })
    )

    this._subs.add(
      this.appFacadeService.getErrorTimeInactive$().pipe(
        combineLatestWith(this.appFacadeService.getLoggedUser$()),
        map(([error, loggedUser]) => error && loggedUser),
        debounceTime(800),
      ).subscribe(error => {
        if (error && !window.location.href.includes("auth")) {
          this.authFacadeService.logOut();

          Swal.fire({
            title: 'Expired Session',
            text: 'Due to inactivity, you have been logged out',
            icon: 'info',
            showConfirmButton: false,
            showCancelButton: false
          })

          setTimeout(() => {
            this.routerFacade.gotoAuth$()
            // window.location.reload()
          }, 500)
        }
      })
    )

    this._subs.add(
      this.locationFacadeService.getCurrentLocation$().pipe(
        tap(location => this.selectedLocation = location),
        switchMap(location => location.id != '' && location.id != null ? timer(0, 30000) : NEVER)
      ).subscribe(_location => {
        this.reviewTargetFacadeService.searchLocationReviewTargetWithErrors({ locationId: this.selectedLocation.id })
      })
    )

    this._subs.add(
      this.facebookFacadeService.getFacebookError$().pipe(
        filter(error => error.showable ? true : false),
      ).subscribe(error => {
        if (error.type == 'toast') {
          this.messageToast.add({
            severity: 'error',
            summary: error.error.error_user_title,
            detail: error.error.error_user_msg,
            life: 3500,
          });
        } else if (error.type == 'pop-up') {
          Swal.fire({
            title: error.error.error_user_title,
            text: error.error.error_user_msg,
            icon: 'error',
            showCancelButton: false,
            confirmButtonText: 'Ok!'
          }).then(_res => {
            if (error.ok_action) {
              this.routerFacade.gotoReviewTargetLocation$();
            }

            this.facebookFacadeService.setFacebookError(emptyFacebookErrorModel())
          })
        }
      })
    );

    this.appFacadeService.getErrorTrialOver$().pipe(
      startWith(false),
      distinctUntilChanged(),
    ).subscribe(trialOver => {
      if (this.trialOverModal && this.selectedBusiness.preferredLocation != '') {
        this.locationFacadeService.findLocationById(this.selectedBusiness.preferredLocation);
      } else {
        this.toogleTrialOverModal(trialOver)
      }
    });

    this._subs.add(
      this.appFacadeService.getErrorTrialOver$().pipe(
        startWith(false),
        distinctUntilChanged(),
        filter(trialOver => trialOver),
        combineLatestWith(this.preferredLocation$),
        filter(([trialOver, preferredLocation]) => trialOver && !!preferredLocation.id)
      ).subscribe(_ => {
        this.toogleTrialOverModal(true)
      })
    );

    // TODO: hidden for now until bugginess resolved, it should not be shown when the user has 0 locations, and it shouldn't conflict with other models
    //this._subs.add(
    //  this.appFacadeService.getLoggedUser$()
    //    .pipe(
    //      delay(5000),
    //      filter(_ => !window.location.href.includes("auth")),
    //      filter(user => user.phoneNumber == null || user.phoneNumber == ""),
    //      filter(user => user.isShowPhoneNumberUpdate == false || user.isShowPhoneNumberUpdate == null),
    //      filter(user => user.id !== null)
    //    )
    //    .subscribe(user => {
    //      this.latestModalREf = this.modalService.open(this.phoneNumberUpdate, { centered: true, size: 'xl', backdrop: "static" });
    //    })
    //)
  }

  authPathValidation() {
    const path = this.location.path();
    if (path.includes('/auth/sign-up')
      || path.includes('/auth/recovery-pass')
      || path.includes('/auth/recovery-pass-confirm')
      || path.includes('/auth/code-verification')
    ) {
      this.authFacadeService.logOut();
    }
  }

  userFullName() {
    if (!this.loggedUser.name && !this.loggedUser.lastName) {
      return ''
    } else if (this.loggedUser.name != '' && this.loggedUser.lastName != '') {
      return this.loggedUser.name + '' + this.loggedUser.lastName
    } else {
      return this.loggedUser.name + this.loggedUser.lastName
    }
  }

  latestModalREf: NgbModalRef = null

  toogleTrialOverModal = (toogle: boolean) => {
    if (toogle) {
      this.latestModalREf = this.modalService.open(this.trialOverModal, { centered: true, backdrop: 'static', keyboard: false });
    } else {
      if (this.latestModalREf) {
        this.latestModalREf.close();
        this.latestModalREf = null;
      }
    }
  }
  updatePhoneNumberCancel() {
    this.modalService.dismissAll()
    this.userFacade.updateShowModalPhoneNumber({ userId: this.loggedUser.id, isShowModal: true })
  }
  savePhoneNumber() {
    this.modalService.dismissAll()
  }

  ngOnDestroy(): void {
    if (this._subs) this._subs.unsubscribe()
  }
}
